import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, reduxForm } from 'redux-form';
import classNames from 'classnames';
import EditIcon from '@material-ui/icons/Edit';

import Typography from '@material-ui/core/Typography';
import HelpIcon from '@material-ui/icons/Help';

import { TRANSLATIONS } from 'translations';

import {
  DEFAULT_LANGUAGE,
  AVAILABLE_LANGUAGES
} from 'pages/create-activity/constants';

import {
  Input,
  MultipleSelectField,
  FormActionButtons,
  DndUploadImage,
  CustomDateRangePicker,
  PopoverWrapper,
  TimePicker,
  ChipsInput,
  CustomColorPicker
} from 'components/index';

import validate from './validate';

import { useStyles } from 'components/general-create/styles';
import DeactivateDatesInput from '../../deactivate-dates-input';

const initialValues = {
  productCode: '',
  color: ''
};

const AccommodationGeneralCreate = ({
  handleSubmit,
  isUpdate,
  setSubmitButton,
  bookDates,
  colorHandler,
  color,
  ...otherProps
}) => {
  const {
    languages,
    selectedLanguage,
    addLanguage,
    deleteLanguage,
    selectLanguage
  } = otherProps;
  const [t] = useTranslation();

  const classes = useStyles();
  const availableLanguages = [...AVAILABLE_LANGUAGES].filter(
    x => !languages.includes(x)
  );
  const [openColorPicker, setOpenColorPicker] = React.useState(false);

  return (
    <div className={classes.wrapper}>
      <Typography variant="h1" className={classes.title}>
        {t('General')}
      </Typography>
      <MultipleSelectField
        availableItems={availableLanguages}
        notAvailableToDeleteItems={[DEFAULT_LANGUAGE]}
        list={languages}
        selectedItem={selectedLanguage}
        addItem={addLanguage}
        deleteItem={deleteLanguage}
        selectItem={selectLanguage}
      />
      <form onSubmit={handleSubmit}>
        <div className={classes.row}>
          <div className={classes.column}>
            <Field
              className={classes.inputIndent}
              name="coverPhoto"
              label="Cover photo *"
              component={DndUploadImage}
            />
          </div>
          <div className={classes.column}>
            <Field
              name="title"
              placeholder="Title"
              className={classes.inputIndent}
              label="Title *"
              component={Input}
            />
            {isUpdate && (
              <Field
                name="productCode"
                disabled
                placeholder="Product code"
                className={classes.inputIndent}
                label="Product code"
                component={Input}
              />
            )}
            <Field
              isOnlyNumber={true}
              maxLength={9}
              name="guests"
              placeholder="Guests"
              label="Guests *"
              className={classes.inputIndent}
              component={Input}
            />
          </div>
        </div>
        <Field
          name="location"
          placeholder="Location"
          label="Location *"
          className={classes.inputIndent}
          component={Input}
        />

        <div className={classes.row}>
          <div className={classNames(classes.column, classes.spaceBetween)}>
            <div className={classes.datePickerWrapper}>
              <Field
                name="availability"
                label="Availability"
                component={CustomDateRangePicker}
              />
            </div>

            <PopoverWrapper text="If you don't select the timestamps, the activity will be available all the time">
              <HelpIcon className={classes.helpIcon} />
            </PopoverWrapper>
          </div>

          <div className={classNames(classes.column, classes.spaceBetween)}>
            <Field
              name="checkInAfter"
              label="Check-in after *"
              isHiddenErrorMsg={true}
              className={classes.timePicker}
              component={TimePicker}
            />

            <Field
              name="checkOutBefore"
              label="Check-out before *"
              isHiddenErrorMsg={true}
              className={classes.timePicker}
              component={TimePicker}
            />
          </div>
        </div>

        <div className={classes.spaceBetween} style={{ marginBottom: 30 }}>
          <Field
            name="deactivatedDates"
            label="Deactivated Days"
            component={DeactivateDatesInput}
            bookDates={bookDates}
          />
        </div>

        <Field
          name="bedDetails"
          id="bedDetails"
          className={classes.inputIndent}
          label={
            <div className={classes.labelWithHelpIcon}>
              <span>{t(TRANSLATIONS[selectedLanguage].bedDetails)}</span>
              <PopoverWrapper
                text={`${t(
                  TRANSLATIONS[selectedLanguage].includedHelpMessage
                )}`}
              >
                <HelpIcon className={classes.chipsHelpIcon} />
              </PopoverWrapper>
            </div>
          }
          placeholder={`Hit Enter to add an item`}
          maxLength="255"
          component={ChipsInput}
        />

        <div className={classes.row}>
          <div className={classes.column}>
            <Field
              name="minNights"
              isOnlyNumber={true}
              maxLength={9}
              placeholder="Number of nights"
              className={classes.inputIndent}
              label="Min. number of nights *"
              component={Input}
            />
          </div>
          <div className={classNames(classes.column, classes.spaceBetween)}>
            <div className={classes.colorPaletteWrapper}>
              <Typography
                variant="h3"
                style={{
                  marginBottom: '6px',
                  textTransform: 'uppercase'
                }}
              >
                {t('Booking tiles color')}
              </Typography>
              <div
                className={classes.colorPalette}
                style={{ backgroundColor: color }}
              >
                <div
                  onClick={() => setOpenColorPicker(true)}
                  className={classes.editIconWrapper}
                >
                  <EditIcon className={classes.editIcon} />
                </div>
                {openColorPicker && (
                  <Field
                    name="color"
                    onClose={setOpenColorPicker}
                    defaultColor={color}
                    onChange={colorHandler}
                    component={CustomColorPicker}
                  />
                )}
              </div>
            </div>
            <div className={classNames(classes.column)}>
              <PopoverWrapper text="This color will be displayed on the booking tiles for accommodations booking on the Show Calendar page.">
                  <HelpIcon className={classes.helpIconColor} />
              </PopoverWrapper>
            </div> 
          </div>
        </div>
        <FormActionButtons
          cancelPath="/accommodations-and-activities"
          actionLabel="Next"
          setSubmitButton={setSubmitButton}
          isUpdate={isUpdate}
        />
      </form>
    </div>
  );
};

export default reduxForm({
  form: 'generalForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(AccommodationGeneralCreate);
